import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Paid } from '@mui/icons-material';
import * as adminApi from '@/api/admin';
import type { SubscriptionRow } from '@/api/admin';
import type { SubscriptionStatus } from '@/api/types';
import * as metricsApi from '@/api/metrics';
import { toApiError } from '@/api/client';
import { LoadingScreen } from '@/components/LoadingScreen';
import { PLAN_LABEL } from '@/lib/labels';
import { formatDate } from '@/lib/formatters';

export const Route = createFileRoute('/_authenticated/admin/suscripciones/')({
  component: AdminSubscriptionsPage,
});

const usdFormat = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const SUB_STATUS: Record<SubscriptionStatus, { label: string; color: 'info' | 'success' | 'error' | 'default' }> = {
  Trial: { label: 'Trial', color: 'info' },
  Active: { label: 'Activa', color: 'success' },
  PastDue: { label: 'En mora', color: 'error' },
  Cancelled: { label: 'Cancelada', color: 'default' },
};

const FILTERS: (SubscriptionStatus | 'all')[] = ['all', 'PastDue', 'Active', 'Trial', 'Cancelled'];

function daysSince(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

function Kpi({ label, value, color }: { label: string; value: string; color?: string }): JSX.Element {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, color }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

function AdminSubscriptionsPage(): JSX.Element {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<SubscriptionStatus | 'all'>('all');

  const subsQ = useQuery({ queryKey: ['admin-subscriptions'], queryFn: adminApi.listSubscriptions });
  const metricsQ = useQuery({ queryKey: ['metrics', 'business'], queryFn: metricsApi.getBusinessMetrics });

  const rows: SubscriptionRow[] = useMemo(() => {
    const all = subsQ.data?.items ?? [];
    return filter === 'all' ? all : all.filter((r) => r.status === filter);
  }, [subsQ.data, filter]);

  if (subsQ.isLoading) return <LoadingScreen message="Cargando suscripciones…" />;
  if (subsQ.isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{toApiError(subsQ.error).message}</Alert>
      </Box>
    );
  }

  const byStatus = subsQ.data?.summary.byStatus ?? {};
  const mrr = metricsQ.data?.mrr;

  return (
    <Box sx={{ p: 3 }}>
      <Stack sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Paid color="primary" /> Suscripciones
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Estado de cobranza cross-tenant. Los morosos van primero — es la cola de dunning.
        </Typography>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Kpi label="MRR" value={mrr != null ? usdFormat.format(mrr) : '—'} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Kpi label="En mora" value={String(byStatus.PastDue ?? 0)} color={(byStatus.PastDue ?? 0) > 0 ? 'error.main' : undefined} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Kpi label="Activas" value={String(byStatus.Active ?? 0)} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Kpi label="Trial" value={String(byStatus.Trial ?? 0)} />
        </Grid>
      </Grid>

      {subsQ.data?.summary.truncated ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Mostrando una muestra (los conteos no son exhaustivos — hay más de {subsQ.data.summary.total} tenants).
        </Alert>
      ) : null}

      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} alignItems="center">
        <Typography variant="body2" color="text.secondary">
          Estado
        </Typography>
        <Select size="small" value={filter} onChange={(e) => setFilter(e.target.value as SubscriptionStatus | 'all')} sx={{ minWidth: 150 }}>
          {FILTERS.map((f) => (
            <MenuItem key={f} value={f}>
              {f === 'all' ? 'Todas' : SUB_STATUS[f].label}
            </MenuItem>
          ))}
        </Select>
      </Stack>

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small" aria-label="Suscripciones">
            <TableHead>
              <TableRow>
                <TableCell>Tenant</TableCell>
                <TableCell>Producto/plan</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Próximo cobro</TableCell>
                <TableCell>En mora</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      Sin suscripciones para el filtro actual.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.tenantId} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{r.name}</TableCell>
                    <TableCell>{PLAN_LABEL[r.plan]}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={SUB_STATUS[r.status].color}
                        variant={r.status === 'PastDue' ? 'filled' : 'outlined'}
                        label={SUB_STATUS[r.status].label}
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(r.nextBillingAt)}</TableCell>
                    <TableCell>
                      {r.pastDueSince ? (
                        <Typography variant="caption" color="error">
                          hace {daysSince(r.pastDueSince)} d
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        onClick={() => navigate({ to: '/admin/tenants/$id', params: { id: r.tenantId } })}
                      >
                        Detalle
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
